import PropTypes from 'prop-types'
import querystring from 'querystring'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { CHECKOUT_SERVICE_ACTIONS } from '../../../../shared/constants.js'
import { logger } from '../../../lib/logging'
import { feathers as app } from '../../../feathers.js'
import BillingAddressModalForm from '../../dialog/BillingAddressModalForm/BillingAddressModalForm.jsx'
import { flattenBillingInfo, unflattenBillingInfo } from './mappers.js'
import PayButton from './PayButton.jsx'

// this should match what the subscription service requires to process taxes
const requiredInputsByCountry = {
  us: ['state', 'postCode', 'vatNumber'],
  default: ['country', 'vatNumber']
}

const hiddenInputsByCountry = {
  us: [],
  default: ['state']
}

export const getRequiredInputs = country =>
  requiredInputsByCountry[country.toLowerCase()] ||
  requiredInputsByCountry.default

export const getHiddenInputs = country =>
  hiddenInputsByCountry[country.toLowerCase()] || hiddenInputsByCountry.default

export const isFormValid = (requiredInputs, inputValues) =>
  requiredInputs.reduce(
    (valid, requiredInputName) =>
      valid && Boolean(inputValues[requiredInputName]),
    true
  )

const HostedPaymentMethodButton = ({ team, user, buttonLabelId, setTeam }) => {
  const [loading, setLoading] = useState(false)
  const [billingModalOpen, setBillingModalOpen] = useState(false)
  const [billingInfo, setBillingInfo] = useState({})
  const [paymentSourcesUrl, setPaymentSourcesUrl] = useState()
  const [hasValidBillingAddress, setHasValidBillingAddress] = useState(false)

  const requiredInputs = useMemo(
    () => getRequiredInputs(team.profile.country),
    [team]
  )

  const hiddenInputs = useMemo(() => getHiddenInputs(team.profile.country), [
    team
  ])

  useEffect(() => {
    let isCurrent = true
    setLoading(true)

    // if team subscription is cancelled, redirect url will contain reactivation flag
    let redirectUrl = window.location.href
    if (team.subscription.status === 'cancelled') {
      const [url, qs] = window.location.href.split('?')
      const queryParams = querystring.parse(qs)
      queryParams.reactivate = 1
      redirectUrl = [url, querystring.stringify(queryParams)].join('?')
    }

    // call service to get billing info + payment method url
    app
      .service('/api/subscription/checkout')
      .get({
        teamId: team.id,
        queryParams: { redirectUrl },
        action: CHECKOUT_SERVICE_ACTIONS.PAYMENT_CONFIG
      })
      .then(({ managePaymentSourcesUrl, billingAddress = {}, vatNumber }) => {
        if (isCurrent) {
          const flatBillingInfo = flattenBillingInfo({
            billingAddress,
            vatNumber
          })

          setPaymentSourcesUrl(managePaymentSourcesUrl)
          setBillingInfo(flatBillingInfo)
          setHasValidBillingAddress(isFormValid(requiredInputs, flatBillingInfo))
        }
      })
      .catch(err =>
        logger.error('HostedPaymentMethodButton checkout.get err', err)
      )
      .finally(() => isCurrent && setLoading(false))

    return () => (isCurrent = false)
  }, [requiredInputs])

  const goToPaymentSetup = useCallback(() => {
    setLoading(true)
    window.location.href = paymentSourcesUrl
  }, [paymentSourcesUrl])

  const handleButtonClick = useCallback(() => {
    setLoading(true)
    if (hasValidBillingAddress) {
      goToPaymentSetup()
    } else {
      setBillingModalOpen(true)
    }
    setLoading(false)
  }, [hasValidBillingAddress, goToPaymentSetup])

  const closeBillingModal = useCallback(() => {
    setBillingModalOpen(false)
  }, [])

  const updateBillingAndGoToPayment = useCallback(async newBillingInfo => {
    // put modal in busy state
    setLoading(true)

    try {
      // service call update billing
      await app.service('/api/subscription/billing').update(team.id, unflattenBillingInfo(newBillingInfo))

      // update mongodb team record if setTeam is set
      if (setTeam) {
        // Patch will only merge root level objects so we fetch the current
        // profile to merge it together with the new billing info.
        const { profile } = await app.service('/api/team').get(team.id)
        const updatedTeam = await app.service('/api/team').patch(team.id, {
          profile: {
            ...profile,
            city: newBillingInfo.city,
            country: newBillingInfo.country,
            postcode: newBillingInfo.postCode,
            state: newBillingInfo.state,
            street: newBillingInfo.line1,
            vatNo: newBillingInfo.vatNumber
          }
        })

        setTeam(updatedTeam.id)
      }

      closeBillingModal()
      goToPaymentSetup()
    } catch (error) {
      logger.error('HostedPaymentMethodButton updateBillingAndGoToPayment error:', error)
    }

    setLoading(false)
  },
  [goToPaymentSetup, closeBillingModal])

  return (
    <>
      <PayButton
        onClick={handleButtonClick}
        loading={loading}
        labelId={buttonLabelId}
      />

      <BillingAddressModalForm
        open={billingModalOpen}
        onCloseClick={closeBillingModal}
        onContinueClick={updateBillingAndGoToPayment}
        team={team}
        user={user}
        hiddenInputs={hiddenInputs}
        requiredInputs={requiredInputs}
        billingInfo={billingInfo}
        disabled={loading}
      />
    </>
  )
}

HostedPaymentMethodButton.propTypes = {
  team: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  buttonLabelId: PropTypes.string,
  setTeam: PropTypes.func
}

export default HostedPaymentMethodButton
